/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from "three";

import { const_var, params } from '../../../../../redux/reducers/reducer';

import { extrudeSettings, railModel } from '../../../../../utils/Functions';
import BaseRailModel from '../../BaseRail';
import PillarRailModel from '../../Legs';
import CommonTruss from '../CommonTruss';
import { MaterialTruss } from '../../../CustomMaterial';
import { trussDimensions } from '../../../../../helpers/building_component/truss/truss.helper';

const roofRailDistanceY = 0.7;
const hangRailDivision = 1 / 2;

const CMT = ({ trussType, normalMap, envMap, trussModelPosInfoArr, overlapTrussArr, hiddenBaseRailIdArr, meshDelModel_CSG, isFor  }) => {

    return (
        <>
            {isFor == 'center' && <BaseRailModel normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} />}
            {isFor == 'center' && <PillarRailModel trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} roofRailDistanceY={roofRailDistanceY} hangRailDivision={hangRailDivision} />}
            <TrussModel trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} isFor={isFor} />
        </>
    )
}

export default CMT

const TrussModel = ({ trussType, trussModelPosInfoArr, normalMap, envMap, overlapTrussArr, hiddenBaseRailIdArr, isFor }) => {
    const obj_centerHRail = new THREE.Object3D();
    const obj_centerVRail = new THREE.Object3D();
    const obj_gapShortVRail = new THREE.Object3D();
    const obj_gapSlopeLRail = new THREE.Object3D();
    const obj_gapSlopeRRail = new THREE.Object3D();
    const obj_underTruss = new THREE.Object3D();
    const vector = new THREE.Vector3(1, 0, 0)

    const ref_centerHRail = useRef();
    const ref_centerVRail = useRef();
    const ref_gapShortVRail = useRef();
    const ref_gapSlopeLRail = useRef();
    const ref_gapSlopeRRail = useRef();
    const ref_underTruss = useRef();

    const connectRailWidth = 0.3;
    const connectRailHeight = 0.2;
    const offset = 0.035;
    const trussDimension = trussDimensions(isFor)

    const alignPosOffset = useMemo(() => {
        if(params.p_r_s === 1 ) return const_var.roofRegularDst * 2;
        else if(params.p_r_s === 2 || params.p_r_s === 3) return 0;    
        else return 0;
    }, [params.p_r_s, trussDimension.roofPitch])
    
    const roofAlpha = Math.atan(trussDimension.roofPitch / 12);
    const roofHeight = (trussDimension.buildingWidth / 2) * (trussDimension.roofPitch / 12);
    const roofOuterRailLength = useMemo(() => (Math.sqrt(Math.pow(trussDimension.buildingWidth / 2, 2) + Math.pow(roofHeight, 2))), [trussDimension.buildingWidth, roofHeight]);
    const roofInnerRailLength = useMemo(() => (roofOuterRailLength / 2), [roofOuterRailLength])
    const bottomBaseRailLength = (trussDimension.buildingWidth - connectRailHeight / 2 - connectRailWidth / 2) * hangRailDivision
    const roofCenterRailHeight = ((trussDimension.buildingWidth / 2) * trussDimension.roofPitch / 12) * hangRailDivision + roofRailDistanceY;

    const connectShortRailModelDataArr = useMemo(() => {
        let vRailDataArr = [];
        let lRailDataArr = [];
        let rRailDataArr = [];

        const distance = 1.5;
        const count = Math.floor((trussDimension.buildingWidth / 4) / distance)
        let lRailLength = 0;
        let rRailLength = 0;

        for (let i = 0; i < count; i++) {
            const pos_x = trussDimension.buildingWidth / 4 + distance * i;
            const pos_y = trussDimension.buildingHeight + roofHeight / 2 + trussDimension.roofPitch / 12 * (trussDimension.buildingWidth / 4 - pos_x) - roofRailDistanceY - connectRailHeight / 2 + 0.02;
            
            const tempVRailData = {
                pos_x: pos_x,
                pos_y: pos_y - 0.015 * (trussDimension.roofPitch - 3),
                length: roofRailDistanceY,
            }

            if (i % 2 === 0 && i !== count - 1) {
                lRailLength = Math.sqrt(Math.pow(roofRailDistanceY, 2) + Math.pow(distance / Math.cos(roofAlpha), 2));
                const tempLRailData = {
                    pos_x: pos_x,
                    pos_y: pos_y + connectRailWidth / trussDimension.roofPitch,
                    length: lRailLength,
                    angle: roofAlpha - Math.atan((roofRailDistanceY - connectRailHeight / 2) / (distance / Math.cos(roofAlpha), 2)),
                }
                lRailDataArr.push(tempLRailData);

            }
            if (i % 2 === 0 && i > 0) {
                rRailLength = Math.sqrt(Math.pow(roofRailDistanceY, 2) + Math.pow(distance / Math.cos(roofAlpha), 2));
                const tempRRailData = {
                    pos_x: pos_x,
                    pos_y: pos_y + connectRailWidth / trussDimension.roofPitch,
                    length: rRailLength,
                    angle: roofAlpha + Math.atan((roofRailDistanceY - connectRailHeight / 2) / (distance / Math.cos(roofAlpha), 2)),
                }
                rRailDataArr.push(tempRRailData);
            }

            vRailDataArr.push(tempVRailData);
        }
        
        const edgeShorRailModel = {
            pos_x: trussDimension.buildingWidth / 2 - (roofRailDistanceY + connectRailWidth * 2) * Math.sin(roofAlpha) * Math.cos(roofAlpha),
            pos_y: trussDimension.buildingHeight - roofRailDistanceY - connectRailHeight / 2 + (roofRailDistanceY + connectRailWidth * 2) * Math.sin(roofAlpha) * Math.sin(roofAlpha),
            length: params.p_r_s === 1 ? 0 : roofRailDistanceY,
        }

        vRailDataArr.push(edgeShorRailModel);

        return {
            vRailDataArr,
            lRailDataArr,
            rRailDataArr,

            lRailLength,
            rRailLength,
        }
    }, [trussDimension.buildingHeight, trussDimension.buildingWidth, roofHeight, trussDimension.roofPitch, params.p_r_s])
    
    const connectCenterRailModelDataArr = useMemo(() => {
        let frRailDataArr = [];
        let sdRailDataArr = [];

        let frRailLength = 0;
        let sdRailLength = 0;

        const availableUnderSize = trussDimension.buildingWidth / 4;
        const availableUpperSize = availableUnderSize + roofRailDistanceY * Math.cos(roofAlpha) * Math.sin(roofAlpha);

        let underDistance = 3.5;

        let frCount = Math.floor((availableUnderSize) / underDistance);

        if (availableUnderSize % underDistance !== 0) {
            if (availableUnderSize - underDistance * frCount >= underDistance / 2) frCount += 1
            underDistance = availableUnderSize / frCount;
        }

        const sdCount = frCount - 1;
        const upperDistance = availableUpperSize / frCount;

        for (let i = 0; i < frCount; i++) {
            const pos_x = underDistance * i;
            const pos_y = trussDimension.buildingHeight + trussDimension.roofPitch / 12 * (trussDimension.buildingWidth / 4) - roofRailDistanceY - connectRailHeight / 2 - 0.015 * (trussDimension.roofPitch - 3);

            const upperPosOffset = i === (frCount - 1) ? 0 : 1;
            const tempWidthFr = upperDistance + upperPosOffset;
            const tempWidthSd = upperDistance - underDistance + upperPosOffset;
            const tempHeight = (trussDimension.buildingWidth / 4 + roofRailDistanceY / Math.tan(roofAlpha) - upperDistance * (i + 1) - upperPosOffset) * (trussDimension.roofPitch / 12);

            frRailLength = Math.sqrt(Math.pow(tempWidthFr, 2) + Math.pow(tempHeight, 2));
            const alphaFr = Math.atan(tempHeight / tempWidthFr);
            sdRailLength = Math.sqrt(Math.pow(tempWidthSd, 2) + Math.pow(tempHeight, 2));
            const alphaSd = Math.atan(tempHeight / tempWidthSd);
            
            const tempFrRailData = {
                id: i,
                pos_x,
                pos_y,
                length: frRailLength,
                alphaFr,
            }
            frRailDataArr.push(tempFrRailData);

            if (i < sdCount) {
                const tempSdRailData = {
                    id: i,
                    pos_x: pos_x + underDistance,
                    pos_y,
                    length: sdRailLength,
                    alphaSd,
                }
                sdRailDataArr.push(tempSdRailData);
            }
        }

        return {
            frRailDataArr,
            sdRailDataArr,
        }
    }, [trussDimension.buildingWidth, trussDimension.buildingHeight, trussDimension.roofPitch, roofAlpha])

    useEffect(() => {
        if (ref_centerHRail.current && ref_centerVRail.current && ref_underTruss.current && ref_gapShortVRail.current && ref_gapSlopeLRail.current) {
            let index_main_truss = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 1) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    //----- roof inner rail-----
                    obj_underTruss.position.set(offsetForDir * (-trussDimension.buildingWidth / 2 + connectRailWidth / 4), trussDimension.buildingHeight - roofRailDistanceY - connectRailHeight / 2 + 0.005 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_underTruss.rotation.y = offsetForDir * Math.PI / 2;
                    obj_underTruss.rotateOnAxis(vector, offsetForDir * roofAlpha);
                    //--------------------------

                    //----- bottom base rail-----
                    obj_centerHRail.position.set(-bottomBaseRailLength / 2, trussDimension.buildingHeight + roofHeight / 2 - roofRailDistanceY - connectRailHeight / 2 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_centerHRail.rotation.y = Math.PI / 2;
                    //---------------------

                    //----- roof center rail-----
                    obj_centerVRail.position.set(0, trussDimension.buildingHeight + roofHeight / 2 - roofRailDistanceY - connectRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_centerVRail.rotation.x = -Math.PI / 2;
                    //---------------------

                    if (hiddenBaseRailIdArr.includes(index_truss)) {
                        obj_underTruss.scale.setScalar(0);
                        obj_centerHRail.scale.setScalar(0);
                        obj_centerVRail.scale.setScalar(0);
                    } else {
                        obj_underTruss.scale.setScalar(1);
                        obj_centerHRail.scale.setScalar(1);
                        obj_centerVRail.scale.setScalar(1);
                    }

                    obj_underTruss.updateMatrix();
                    ref_underTruss.current.setMatrixAt(index_main_truss, obj_underTruss.matrix);
                    obj_centerVRail.updateMatrix();
                    ref_centerVRail.current.setMatrixAt(index_main_truss, obj_centerVRail.matrix);
                    obj_centerHRail.updateMatrix();
                    ref_centerHRail.current.setMatrixAt(index_main_truss, obj_centerHRail.matrix);

                    index_main_truss ++;
                })
            })

            let index_gap_short_v_rail = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    new Array(connectShortRailModelDataArr.vRailDataArr.length).fill('').forEach((_, index_rail) => {
                        obj_gapShortVRail.position.set(offsetForDir * connectShortRailModelDataArr.vRailDataArr[index_rail].pos_x, connectShortRailModelDataArr.vRailDataArr[index_rail].pos_y + alignPosOffset, trussModelPosInfoArr[index_truss]);
                        obj_gapShortVRail.rotation.x = -Math.PI / 2;
                        obj_gapShortVRail.rotation.y = offsetForDir * roofAlpha;

                        if (hiddenBaseRailIdArr.includes(index_truss)) {
                            obj_gapShortVRail.scale.setScalar(0);
                        } else {
                            obj_gapShortVRail.scale.setScalar(1);
                        }

                        if (connectShortRailModelDataArr.vRailDataArr[index_rail].length === 0) obj_gapShortVRail.scale.setScalar(0);

                        obj_gapShortVRail.updateMatrix();
                        ref_gapShortVRail.current.setMatrixAt(index_gap_short_v_rail, obj_gapShortVRail.matrix);

                        index_gap_short_v_rail ++;
                    })
                })
            })
            
            let index_gap_slope_l_rail = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    new Array(connectShortRailModelDataArr.lRailDataArr.length).fill('').forEach((_, index_rail) => {
                        obj_gapSlopeLRail.position.set(offsetForDir * connectShortRailModelDataArr.lRailDataArr[index_rail].pos_x, connectShortRailModelDataArr.lRailDataArr[index_rail].pos_y + alignPosOffset, trussModelPosInfoArr[index_truss]);
                        obj_gapSlopeLRail.rotation.y = offsetForDir * Math.PI / 2;
                        obj_gapSlopeLRail.rotateOnAxis(vector, offsetForDir * connectShortRailModelDataArr.lRailDataArr[index_rail].angle);

                        if (hiddenBaseRailIdArr.includes(index_truss)) {
                            obj_gapSlopeLRail.scale.setScalar(0);
                        } else {
                            obj_gapSlopeLRail.scale.setScalar(1);
                        }

                        obj_gapSlopeLRail.updateMatrix();
                        ref_gapSlopeLRail.current.setMatrixAt(index_gap_slope_l_rail, obj_gapSlopeLRail.matrix);

                        index_gap_slope_l_rail ++;
                    })
                })
            })
            
            let index_gap_slope_r_rail = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    new Array(connectShortRailModelDataArr.rRailDataArr.length).fill('').forEach((_, index_rail) => {
                        obj_gapSlopeRRail.position.set(offsetForDir * connectShortRailModelDataArr.rRailDataArr[index_rail].pos_x, connectShortRailModelDataArr.rRailDataArr[index_rail].pos_y + alignPosOffset, trussModelPosInfoArr[index_truss]);
                        obj_gapSlopeRRail.rotation.y = offsetForDir * -Math.PI / 2;
                        obj_gapSlopeRRail.rotateOnAxis(vector, -offsetForDir * connectShortRailModelDataArr.rRailDataArr[index_rail].angle);

                        if (hiddenBaseRailIdArr.includes(index_truss)) {
                            obj_gapSlopeRRail.scale.setScalar(0);
                        } else {
                            obj_gapSlopeRRail.scale.setScalar(1);
                        }

                        obj_gapSlopeRRail.updateMatrix();
                        ref_gapSlopeRRail.current.setMatrixAt(index_gap_slope_r_rail, obj_gapSlopeRRail.matrix);

                        index_gap_slope_r_rail ++;
                    })
                })
            })
            
            ref_centerHRail.current.instanceMatrix.needsUpdate = true;
            ref_centerVRail.current.instanceMatrix.needsUpdate = true;
            ref_underTruss.current.instanceMatrix.needsUpdate = true;
            ref_gapShortVRail.current.instanceMatrix.needsUpdate = true;
            ref_gapSlopeLRail.current.instanceMatrix.needsUpdate = true;
            ref_gapSlopeRRail.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_centerHRail.current, ref_centerVRail.current, ref_underTruss.current, ref_gapShortVRail.current, ref_gapSlopeLRail.current, trussDimension.buildingWidth, trussDimension.buildingHeight, connectShortRailModelDataArr, trussModelPosInfoArr, overlapTrussArr, hiddenBaseRailIdArr, roofHeight, alignPosOffset, roofAlpha, bottomBaseRailLength])

    return (
        <group position={[0, -offset, 0]} rotation={[0,trussDimension.trussRotation,0]} name='CMT-truss-legs'>
            <CommonTruss trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} normalMap={normalMap} envMap={envMap} roofRailDistanceY={roofRailDistanceY} isFor={isFor}/>
            {/* --- roof under truss model --- */}
            <instancedMesh ref={ref_underTruss} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
                <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(roofInnerRailLength - 0.01 )]} />
                <MaterialTruss normalMap={normalMap} envMap={envMap} />
            </instancedMesh>
            {/* ------------------------- */}

            {/* --- bottom base rail model --- */}
            <instancedMesh instancedMesh ref={ref_centerHRail} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
                <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(bottomBaseRailLength)]} />
                <MaterialTruss normalMap={normalMap} envMap={envMap} />
            </instancedMesh>
            {/* ------------------------- */}

            {/* --- roof center rail model --- */}
            <instancedMesh ref={ref_centerVRail} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
                <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(roofCenterRailHeight)]} />
                <MaterialTruss normalMap={normalMap} envMap={envMap} />
            </instancedMesh>
            {/* ------------------------- */}

            {/* --- connect rail model --- */}
            <instancedMesh ref={ref_gapShortVRail} args={[null, null, trussModelPosInfoArr.length * connectShortRailModelDataArr.vRailDataArr.length * 2]} frustumCulled={false}>
                <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(roofRailDistanceY)]} />
                <MaterialTruss normalMap={normalMap} envMap={envMap} />
            </instancedMesh>
            <instancedMesh ref={ref_gapSlopeLRail} args={[null, null, trussModelPosInfoArr.length * connectShortRailModelDataArr.lRailDataArr.length * 2]} frustumCulled={false}>
                <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(connectShortRailModelDataArr.lRailLength)]} />
                <MaterialTruss normalMap={normalMap} envMap={envMap} />
            </instancedMesh>
            <instancedMesh ref={ref_gapSlopeRRail} args={[null, null, trussModelPosInfoArr.length * connectShortRailModelDataArr.rRailDataArr.length * 2]} frustumCulled={false}>
                <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(connectShortRailModelDataArr.rRailLength)]} />
                <MaterialTruss normalMap={normalMap} envMap={envMap} />
            </instancedMesh>
            {/* ------------------------- */}
            
            {connectCenterRailModelDataArr.frRailDataArr.map((item, index) => (
                <group name='CMT-truss-rail-legs'>
                    <FrConnectRailModel
                        index={index}
                        trussModelPosInfoArr={trussModelPosInfoArr}
                        connectRailModelData={item}
                        hiddenBaseRailIdArr={hiddenBaseRailIdArr}
                        overlapTrussArr={overlapTrussArr}
                        roofAlpha={roofAlpha}
                        connectRailWidth={connectRailWidth}
                        connectRailHeight={connectRailHeight}
                        normalMap={normalMap}
                        envMap={envMap}
                        alignPosOffset={alignPosOffset}
                        isFor={isFor}
                    />
                </group>
            ))}
            {connectCenterRailModelDataArr.sdRailDataArr.map((item, index) => (
                <group name='CMT-truss-rail-legs'>
                    <SdConnectRailModel
                        index={index}
                        trussModelPosInfoArr={trussModelPosInfoArr}
                        connectRailModelData={item}
                        hiddenBaseRailIdArr={hiddenBaseRailIdArr}
                        overlapTrussArr={overlapTrussArr}
                        roofAlpha={roofAlpha}
                        connectRailWidth={connectRailWidth}
                        connectRailHeight={connectRailHeight}
                        normalMap={normalMap}
                        envMap={envMap}
                        alignPosOffset={alignPosOffset}
                        isFor={isFor}
                    />
                </group>
            ))}
        </group>
    )
}

const FrConnectRailModel = ({ index, trussModelPosInfoArr, connectRailModelData, hiddenBaseRailIdArr, overlapTrussArr, roofAlpha, connectRailWidth, connectRailHeight, normalMap, envMap, alignPosOffset, isFor }) =>{
    const obj_model = new THREE.Object3D();
    const ref_model = useRef();
    const vector = new THREE.Vector3(1, 0, 0)
    const trussDimension = trussDimensions(isFor)

    const updateInstancedMesh = useCallback(() => {
        if (ref_model.current) {
            let index_all = 0;
            new Array(2).fill("").forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    obj_model.position.set(offsetForDir * connectRailModelData.pos_x, connectRailModelData.pos_y + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_model.rotation.y = offsetForDir * Math.PI / 2;
                    obj_model.rotateOnAxis(vector, offsetForDir * -connectRailModelData.alphaFr);

                    if (hiddenBaseRailIdArr.includes(index_truss)) {
                        obj_model.scale.setScalar(0);
                    } else {
                        obj_model.scale.setScalar(1);
                    }

                    obj_model.updateMatrix();
                    ref_model.current.setMatrixAt(index_all, obj_model.matrix);
                    
                    index_all ++;
                })
            })
            ref_model.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_model.current, trussDimension.buildingWidth, trussDimension.buildingHeight, params.singleSlope, params.freeStandingSide, trussModelPosInfoArr, overlapTrussArr, trussDimension.buildingWidth, trussDimension.buildingHeight, roofAlpha, connectRailModelData, hiddenBaseRailIdArr, alignPosOffset])
    
    useLayoutEffect(() => {
        updateInstancedMesh();
    }, [updateInstancedMesh]);

    
    return (
        <instancedMesh ref={ref_model} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
            <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(connectRailModelData.length)]} />
            <MaterialTruss normalMap={normalMap} envMap={envMap} />
        </instancedMesh>
    )
}


const SdConnectRailModel = ({ index, trussModelPosInfoArr, connectRailModelData, hiddenBaseRailIdArr, overlapTrussArr, roofAlpha, connectRailWidth, connectRailHeight, normalMap, envMap, alignPosOffset, isFor }) =>{
    const obj_model = new THREE.Object3D();
    const ref_model = useRef();
    const vector = new THREE.Vector3(1, 0, 0)
    const trussDimension = trussDimensions(isFor)

    const updateInstancedMesh = useCallback(() => {
        if (ref_model.current) {
            let index_all = 0;
            new Array(2).fill("").forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    obj_model.position.set(offsetForDir * connectRailModelData.pos_x, connectRailModelData.pos_y + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_model.rotation.y = offsetForDir * Math.PI / 2;
                    obj_model.rotateOnAxis(vector, offsetForDir * -connectRailModelData.alphaSd);

                    if (hiddenBaseRailIdArr.includes(index_truss)) {
                        obj_model.scale.setScalar(0);
                    } else {
                        obj_model.scale.setScalar(1);
                    }

                    obj_model.updateMatrix();
                    ref_model.current.setMatrixAt(index_all, obj_model.matrix);
                    
                    index_all ++;
                })
            })
            ref_model.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_model.current, trussDimension.buildingWidth, trussDimension.buildingHeight, params.singleSlope, params.freeStandingSide, trussModelPosInfoArr, overlapTrussArr, trussDimension.buildingWidth, trussDimension.buildingHeight, roofAlpha, connectRailModelData, hiddenBaseRailIdArr, alignPosOffset])
    
    useLayoutEffect(() => {
        updateInstancedMesh();
    }, [updateInstancedMesh]);
    
    return (
        <instancedMesh ref={ref_model} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
            <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(connectRailModelData.length)]} />
            <MaterialTruss normalMap={normalMap} envMap={envMap} />
        </instancedMesh>
    )
}